import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, CheckCircle2, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { Form } from 'react-bootstrap';
import axios from '../../../../../services/AxiosInstance';
import UnmappedAccountsPanel from '../UnmappedAccountsPanel';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { sourceLabel } from '../sourceLabel';

/**
 * Step 1 — the pre-flight report.
 *
 * This used to be a static list of five claims the user ticked while the system
 * checked nothing. Three of them were already computed elsewhere in the
 * product: the earlier-period state (G25/R-BOX-3), the kontantmetod year-end
 * pull-in (R-MET-2) and the skattekonto reconciliation (G23). Asking someone to
 * verify what we have already determined is worse than telling them — and a UI
 * that presents a checkmark for a check it never ran is the same thing as a
 * save that reports success without writing.
 *
 * So the findings below are ANSWERS, read from the endpoints that already
 * compute them. Nothing here re-implements a rule.
 *
 * Two things it deliberately does not do:
 *   · It never blocks. Consistent with the G25 decision — an unclosed earlier
 *     period is worth saying out loud and never worth refusing over.
 *   · It never claims to have checked what it cannot. Whether an invoice exists
 *     that has not reached LedgerLoop is unknowable from inside it, so that
 *     stays a question to a human, asked plainly, at the bottom.
 */

const STATUS = { OK: 'ok', WARN: 'warn', UNKNOWN: 'unknown' };

/** The concrete tier-1 list behind the year-end finding, one line per invoice. */
function yearEndInvoiceLines(details) {
    if (!details || details.tier !== 'ledger') return null;
    const kr = (n) => new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Number(n || 0));
    const lines = [];
    for (const inv of details.customer?.invoices || []) {
        lines.push(`Kundfaktura ${inv.id || '—'} (${inv.invoice_date}): ${kr(inv.total)} kr, moms ${kr(inv.vat)} kr`
            + (inv.paid_by_year_end ? `, ${kr(inv.paid_by_year_end)} kr betalt före årsskiftet` : ''));
    }
    for (const inv of details.supplier?.invoices || []) {
        lines.push(`Leverantörsfaktura ${inv.id || '—'} (${inv.invoice_date}): ${kr(inv.total)} kr`
            + (inv.vat === null ? ', moms ej bokförd' : `, moms ${kr(inv.vat)} kr`)
            + (inv.paid_by_year_end ? `, ${kr(inv.paid_by_year_end)} kr betalt före årsskiftet` : ''));
    }
    return lines.length ? lines : null;
}

function Finding({ status, title, detail, sourceUrl, invoices }) {
    const Icon = status === STATUS.OK ? CheckCircle2
        : status === STATUS.WARN ? AlertTriangle : HelpCircle;
    const color = status === STATUS.OK ? 'var(--primary)'
        : status === STATUS.WARN ? 'var(--bs-warning)' : 'var(--secondary)';
    return (
        <li className="preflight-finding">
            <Icon size={17} color={color} className="preflight-icon" />
            <div>
                <div className="preflight-finding-title">{title}</div>
                {detail && <div className="preflight-finding-detail">{detail}</div>}
                {invoices && (
                    <ul className="preflight-finding-detail mb-0 ps-3">
                        {invoices.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                )}
                {sourceUrl && (
                    <a className="preflight-rule" href={sourceUrl} target="_blank" rel="noopener noreferrer">
                        {sourceLabel(sourceUrl)}
                    </a>
                )}
            </div>
        </li>
    );
}

const InstructionsStep = ({ companyId, vatPeriodId, vatPeriod }) => {
    const [findings, setFindings] = useState(null);
    const [preflight, setPreflight] = useState(null);
    const [confirmed, setConfirmed] = useState(false);

    const load = useCallback(async () => {
        if (!vatPeriod || !vatPeriodId) return;
        const results = [];

        // #5 — earlier open periods (G25 / R-BOX-3). Served alongside the
        // nil-return status; the same warning the review step shows.
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/nil-return`),
                { withCredentials: true }
            );
            const warning = (response.data.warnings || [])
                .find((w) => w.code === 'VAT_EARLIER_PERIOD_OPEN');
            results.push(warning
                ? {
                    status: STATUS.WARN,
                    // The count lives in the server's message, so a hardcoded plural
                    // heading sat above a singular sentence: "Tidigare momsperioder
                    // är fortfarande öppna" / "1 tidigare momsperiod är fortfarande
                    // öppen".
                    title: (warning.periods?.length || 0) === 1
                        ? 'En tidigare momsperiod är fortfarande öppen'
                        : 'Tidigare momsperioder är fortfarande öppna',
                    detail: warning.message,
                    sourceUrl: warning.sourceUrl,
                }
                : {
                    status: STATUS.OK,
                    title: 'Alla tidigare momsperioder är hanterade',
                    detail: 'Ingen tidigare period ligger kvar öppen.',
                });
        } catch (error) {
            console.error('Pre-flight check failed: Tidigare momsperioder kunde inte kontrolleras', error);
            results.push({
                status: STATUS.UNKNOWN,
                title: 'Tidigare momsperioder kunde inte kontrolleras',
            });
        }

        // #4 — kontantmetodens årsskiftesregel (R-MET-2), and #3's sibling
        // checks, all from the validation the review step already runs.
        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/vat-validation`), {
                params: {
                    start_date: vatPeriod.start_date,
                    end_date: vatPeriod.end_date,
                    fiscal_year_id: vatPeriod.fiscal_year_id,
                },
                withCredentials: true,
            });
            const allWarnings = response.data.validation?.warnings || [];

            // A pro rata andel is applied provisionally through the year and
            // trued against the actual turnover at year end. Same rail as the
            // årsskiftes line below, and only raised on the period that contains
            // räkenskapsårets sista dag.
            const truing = allWarnings.find((w) => w.type === 'pro_rata_year_end_truing');
            if (truing) {
                results.push({
                    status: STATUS.WARN,
                    title: 'Avdragsandelen bör stämmas av mot årets omsättning',
                    detail: truing.message,
                });
            }

            // S6 — the ML 7 kap. 16 § check is computed where the ledger allows
            // it (tier 1): a clean year end arrives as a positive confirmation
            // that REPLACES the warning; unpaid invoices arrive with the list.
            // SIE-only companies (tier 2) keep the reminder, sharpened by the
            // reskontra balances. R-MET-2 and the Riksdagen link stay on all.
            const warning = allWarnings
                .find((w) => w.type === 'cash_method_year_end_pull_in');
            const clear = allWarnings
                .find((w) => w.type === 'cash_method_year_end_clear');
            results.push(warning
                ? {
                    status: STATUS.WARN,
                    title: warning.details?.tier === 'ledger'
                        ? 'Obetalda fakturor vid årsskiftet ska ingå i den här perioden'
                        : 'Perioden innehåller räkenskapsårets sista dag',
                    detail: warning.message,
                    sourceUrl: warning.sourceUrl,
                    invoices: yearEndInvoiceLines(warning.details),
                }
                : clear
                    ? {
                        status: STATUS.OK,
                        title: 'Inga obetalda fakturor vid årsskiftet',
                        detail: clear.message,
                        sourceUrl: clear.sourceUrl,
                    }
                    : {
                        status: STATUS.OK,
                        title: 'Ingen årsskiftesjustering behövs för den här perioden',
                        detail: 'Antingen tillämpas faktureringsmetoden, eller så innehåller perioden '
                            + 'inte räkenskapsårets sista dag.',
                    });
        } catch (error) {
            console.error('Pre-flight check failed: Redovisningsmetodens årsskiftesregel kunde inte kontrolleras', error);
            results.push({
                status: STATUS.UNKNOWN,
                title: 'Redovisningsmetodens årsskiftesregel kunde inte kontrolleras',
            });
        }

        // #3 — skattekontoavstämningen (G23). Only meaningful when the tenant
        // actually imports skattekontoutdrag; otherwise there is nothing to
        // reconcile against and saying "OK" would be a false green.
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/vat-periods/${vatPeriodId}/reconciliation`),
                { withCredentials: true }
            );
            const status = (response.data?.data || response.data || {}).reconciliation;
            if (!status || Number(status.matchedStatements || 0) === 0) {
                results.push({
                    status: STATUS.UNKNOWN,
                    title: 'Skattekontot är inte avstämt för perioden',
                    detail: 'Inga matchade kontohändelser finns. Importera skattekontoutdrag om du '
                        + 'vill stämma av betalningen mot Skatteverkets konto.',
                });
            } else {
                results.push(status.isReconciled
                    ? {
                        status: STATUS.OK,
                        title: 'Skattekontot stämmer med perioden',
                        detail: `${status.matchedStatements} matchad(e) kontohändelse(r).`,
                    }
                    : {
                        status: STATUS.WARN,
                        title: 'Skattekontot stämmer inte med perioden',
                        detail: `Deklarerat ${Math.round(Number(status.expectedPayment || 0))} kr, `
                            + `bokfört ${Math.round(Number(status.actualPayment || 0))} kr.`,
                    });
            }
        } catch (error) {
            console.error('Pre-flight check failed: Skattekontoavstämningen kunde inte kontrolleras', error);
            results.push({
                status: STATUS.UNKNOWN,
                title: 'Skattekontoavstämningen kunde inte kontrolleras',
            });
        }

        setFindings(results);

        // Tier 2 — what is sitting unfinished inside the period. Its own
        // endpoint, and its own failure mode: if the whole call fails the
        // section simply does not render, rather than taking the step with it.
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/preflight`),
                { withCredentials: true }
            );
            if (response.data.success) setPreflight(response.data);
        } catch (error) {
            setPreflight(null);
        }
    }, [companyId, vatPeriodId, vatPeriod]);

    useEffect(() => { load(); }, [load]);

    const allClear = findings && findings.every((f) => f.status === STATUS.OK)
        && (!preflight || preflight.attention === 0);
    const anyUnknown = (findings && findings.some((f) => f.status === STATUS.UNKNOWN))
        || (preflight && preflight.unknown > 0);

    return (
        <div className="step-container">
            <style>{`
                /* No card here. The stepper already puts this content in one,
                   and a second panel inside it just boxed the text in grey. */
                .instructions-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .instructions-intro {
                    color: var(--secondary);
                    font-size: 0.9rem;
                    margin-bottom: 1rem;
                }
                .preflight-list { list-style: none; padding: 0; margin: 0 0 1rem 0; }
                .preflight-finding {
                    display: flex;
                    gap: 0.6rem;
                    align-items: flex-start;
                    padding: 0.6rem 0;
                    border-bottom: 1px solid #e2e8f0;
                }
                .preflight-finding:last-child { border-bottom: none; }
                .preflight-icon { flex-shrink: 0; margin-top: 0.1rem; }
                .preflight-finding-title { font-size: 0.9rem; color: #1e293b; font-weight: 500; }
                .preflight-finding-detail {
                    font-size: 0.82rem;
                    color: var(--secondary);
                    margin-top: 0.15rem;
                }
                .preflight-rule { font-size: 0.78rem; }
                .preflight-ask {
                    border-top: 1px solid #e2e8f0;
                    padding-top: 0.9rem;
                    margin-top: 0.25rem;
                }
                .preflight-ask-note {
                    font-size: 0.82rem;
                    color: var(--secondary);
                    margin-top: 0.3rem;
                }
            `}</style>

            <div>
                {/* Koppla konton — accounts whose money would not reach a box.
                    Above the pre-flight findings because it is the one item here
                    that blocks locking, and it is the same kind of "look at this
                    before you file" as the rest of the step. */}
                <UnmappedAccountsPanel
                    companyId={companyId}
                    vatPeriodId={vatPeriodId}
                />

                <div className="instructions-title">
                    <ClipboardCheck size={18} color="var(--primary)" />
                    Innan du börjar
                </div>

                {!findings ? (
                    <p className="instructions-intro">
                        <Loader2 size={14} className="me-1" />
                        Kontrollerar perioden…
                    </p>
                ) : (
                    <>
                        <p className="instructions-intro">
                            {allClear
                                ? 'Det vi kan kontrollera ser bra ut.'
                                : 'Det här hittade vi när vi gick igenom perioden.'}
                        </p>

                        <ul className="preflight-list">
                            {findings.map((finding, index) => (
                                <Finding key={`${finding.title}-${index}`} {...finding} />
                            ))}
                        </ul>

                        {/* Tier 2 — evidence of incompleteness. We cannot prove
                            nothing is missing, but we can list what looks
                            unfinished inside the period, so the question below
                            is asked against a short list rather than a blank
                            page. A group we could not check says so. */}
                        {preflight && (
                            <ul className="preflight-list">
                                {preflight.groups.map((group) => (
                                    <Finding
                                        key={group.key}
                                        status={group.status === 'attention' ? STATUS.WARN
                                            : group.status === 'clear' ? STATUS.OK : STATUS.UNKNOWN}
                                        title={group.status === 'attention'
                                            ? `${group.count} ${group.label.toLowerCase()}`
                                            : group.label}
                                        detail={group.status === 'attention' ? group.hint
                                            : group.status === 'unknown'
                                                ? (group.reason || 'Kunde inte kontrolleras.')
                                                : 'Inget att åtgärda i perioden.'}
                                    />
                                ))}
                            </ul>
                        )}

                        {/* Tier 3 — the one thing that cannot be checked from
                            inside the system, asked as a question rather than
                            presented as a checkmark. */}
                        <div className="preflight-ask">
                            <Form.Check
                                type="checkbox"
                                id="preflight-confirm"
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                label="Finns det fakturor eller kvitton som ännu inte finns i LedgerLoop? Nej, allt för perioden är med."
                            />
                            <div className="preflight-ask-note">
                                Det här är det enda vi inte kan kontrollera åt dig — ett kvitto som
                                ligger kvar i någons inkorg syns inte här.
                                {anyUnknown && ' Punkterna ovan som inte kunde kontrolleras är också '
                                    + 'värda en egen titt.'}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InstructionsStep;
